import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Tooltip,
  Button,
  Popconfirm,
  Input,
  Form,
  Select,
  Cascader,
  Row,
  Col,
  Space,
  Card,
  Tag,
  Divider,
  Typography } from 'antd';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Icons from '@/components/third-party/Icons';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/App';
import { defaultColors } from '@/themes/antd-theme';
import {
  bp_parameters_data,
  bp_add_parameter_data,
  bp_units_data,
  update_delete_parameter,
  get_control_category_with_sub_categories,
  get_sub_control_category,
  assign_sub_control_category,
  get_machine_list,
} from '../../../utils/api';
import DataTable from '@/components/DataTable';
import PageHeader, { EmptyState } from '@/components/PageHeader';

const { Option } = Select;

const ProcessControlConfig = ({ show_config }) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const textColor = isDark ? '#ffffff' : '#1f2937';

  const [showForm, setShowForm] = useState(false);
  const [formEdit, setFormEdit] = useState(false);
  const [unitList, setUnitList] = useState([]);
  const [parameterData, setParameterData] = useState([]);
  const [editValues, setEditValues] = useState({});
  const [controlCategoryList, setControlCategoryList] = useState([]);
  const [cascaderCatValue, setCascaderCatValue] = useState(null);
  const [subCategoryList, setSubCategoryList] = useState([]);
  const [newSubCatName, setNewSubCatName] = useState('');
  const [addingSubCat, setAddingSubCat] = useState(false);
  const [subCatLoading, setSubCatLoading] = useState(false);
  const [machineList, setMachineList] = useState([]);

  const successUnitList = (res) => setUnitList(res?.data?.data || []);
  const successParameterList = (res) => setParameterData(res?.data?.data || []);

  const getAllData = async () => {
    try {
      await bp_units_data(successUnitList);
      await bp_parameters_data(successParameterList);
      await get_control_category_with_sub_categories((res) => setControlCategoryList(res?.data?.data || []));
      await get_machine_list((res) => setMachineList(res?.data?.data || []));
    } catch (e) {
      console.error('Error fetching data:', e);
      toast.error(e?.response?.data?.message || 'Failed to load process controls');
    }
  };

  const loadSubCategories = () => {
    setSubCatLoading(true);
    get_sub_control_category((res) => {
      setSubCategoryList(res?.data?.data || []);
      setSubCatLoading(false);
    });
  };

  useEffect(() => {
    getAllData();
    loadSubCategories();
  }, []);

  useEffect(() => {
    setEditValues({});
    setFormEdit(false);
  }, [show_config]);

  const handleAdd = async (values, resetForm) => {
    try {
      if (formEdit) {
        await update_delete_parameter(
          { ...values, id: editValues.id, update: true },
          handleSuccess(resetForm)
        );
      } else {
        await bp_add_parameter_data(values, handleSuccess(resetForm));
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleDelete = async (record) => {
    try {
      await update_delete_parameter({ id: record.id }, handleSuccess());
    } catch (e) {
      console.error(e);
    }
  };

  const handleSuccess = (resetForm) => (res) => {
    if (res.status === 'failed') {
      toast.warning(res.message);
    } else {
      toast.success(res.message);
      getAllData();
      setFormEdit(false);
      setEditValues({});
      setShowForm(false);
      if (resetForm) resetForm();
    }
  };

  const handleAddSubCategory = async () => {
    if (!newSubCatName.trim()) return;
    setAddingSubCat(true);
    await assign_sub_control_category({ name: newSubCatName.trim() }, (res) => {
      if (res.status === 'success') {
        toast.success('Sub category added');
        setNewSubCatName('');
        loadSubCategories();
      } else {
        toast.warning(res.message || 'Failed to add sub category');
      }
    });
    setAddingSubCat(false);
  };

  const allColumns = [
    {
      key: 'parameter_name',
      title: 'Control Name',
      dataIndex: 'parameter_name',
      defaultVisible: true,
      sortType: 'string',
      align: 'left',
    },
    {
      key: 'data_type',
      title: 'Data Type',
      dataIndex: 'data_type',
      defaultVisible: true,
      sortType: 'string',
      width: 130,
      align: 'center',
      render: (text) => (text ? <Tag color="purple">{text}</Tag> : '-'),
    },
    {
      key: 'unit',
      title: 'Unit (UOM)',
      dataIndex: 'unit',
      defaultVisible: true,
      sortType: 'string',
      width: 110,
      align: 'center',
      render: (text) => (text ? <Tag color="blue">{text}</Tag> : '-'),
    },
    {
      key: 'control_category',
      title: 'Control Category',
      dataIndex: 'control_category',
      defaultVisible: true,
      sortType: 'string',
      width: 140,
      align: 'center',
      render: (text) => (text ? <Tag color="geekblue">{text}</Tag> : '-'),
    },
    {
      key: 'setpoint',
      title: 'Setpoint',
      dataIndex: 'setpoint',
      defaultVisible: true,
      sortType: 'string',
      width: 100,
      align: 'center',
      render: (text) => text || '-',
    },
    {
      key: 'lower_limit',
      title: 'Lower Limit',
      dataIndex: 'lower_limit',
      defaultVisible: true,
      sortType: 'number',
      width: 110,
      align: 'center',
      render: (text) =>
        text !== undefined && text !== null && text !== '' ? (
          <Tag color="orange">{text}</Tag>
        ) : (
          '-'
        ),
    },
    {
      key: 'upper_limit',
      title: 'Upper Limit',
      dataIndex: 'upper_limit',
      defaultVisible: true,
      sortType: 'number',
      width: 110,
      align: 'center',
      render: (text) =>
        text !== undefined && text !== null && text !== '' ? (
          <Tag color="volcano">{text}</Tag>
        ) : (
          '-'
        ),
    },
    {
      key: 'data_source',
      title: 'Data Source',
      dataIndex: 'data_source',
      defaultVisible: true,
      sortType: 'string',
      width: 130,
      align: 'center',
      render: (text) => (text ? <Tag color="cyan">{text}</Tag> : '-'),
    },
    {
      key: 'criticality',
      title: 'Criticality',
      dataIndex: 'criticality',
      defaultVisible: true,
      sortType: 'string',
      width: 140,
      align: 'center',
      render: (text) =>
        text === 'Critical (CPP)' ? (
          <Tag color="red">Critical (CPP)</Tag>
        ) : text ? (
          <Tag color="default">{text}</Tag>
        ) : (
          '-'
        ),
    },
    {
      key: 'action',
      title: 'Action',
      dataIndex: 'action',
      defaultVisible: true,
      alwaysVisible: true,
      width: 100,
      align: 'center',
      fixed: 'right',
      render: (text, record) => (
        <Space>
          <Tooltip title="Edit">
            <Icons.Pencil
              onClick={() => {
                setFormEdit(true);
                setShowForm(true);
                setEditValues({
                  parameter_name: record.parameter_name,
                  data_type: record.data_type,
                  unit_id: record.unit_id,
                  control_category: record.control_category || '',
                  control_category_id: record.control_category_id || null,
                  sub_category_id: record.sub_category_id || null,
                  setpoint: record.setpoint || '',
                  lower_limit: record.lower_limit || '',
                  upper_limit: record.upper_limit || '',
                  data_source: record.data_source || '',
                  criticality: record.criticality || '',
                  id: record.id,
                });
                setCascaderCatValue(
                  record.control_category_id
                    ? record.sub_category_id
                      ? [record.control_category_id, record.sub_category_id]
                      : [record.control_category_id]
                    : null
                );
              }}
              className="text-amber-600 w-5 h-5 cursor-pointer hover:text-amber-800"
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure to delete this parameter?"
            onConfirm={() => handleDelete(record)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Icons.Trash2 className="text-red-600 w-5 h-5 cursor-pointer hover:text-red-800" />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const validationSchema = Yup.object().shape({
    parameter_name: Yup.string().required('Parameter name is required'),
    data_type: Yup.string().required('Data type is required'),
    unit_id: Yup.number().nullable(),
  });

  return (
    <Card
      className="m-2.5 border border-gray-300 shadow-md"
      style={{ backgroundColor: isDark ? defaultColors.palette.gray[800] : '#ffffff' }}
      title={
        <PageHeader
          compact
          icon={<Icons.Settings size={22} />}
          title={t('Process Control')}
          description="Process-control parameters with limits, data source and machine mapping."
        />
      }
      extra={
        showForm ? (
          <Button
            icon={<Icons.X className="w-4 h-4" />}
            onClick={() => { setShowForm(false); setFormEdit(false); setEditValues({}); setCascaderCatValue(null); }}
          >
            Close form
          </Button>
        ) : (
          <Button type="primary" icon={<Icons.Plus className="w-4 h-4" />} onClick={() => setShowForm(true)}>
            Add Process Control
          </Button>
        )
      }
    >
      {showForm && (
        <div
          className="rounded-xl border mb-5"
          style={{
            backgroundColor: isDark ? defaultColors.palette.gray[700] : '#f8faff',
            borderColor: formEdit ? '#f59e0b' : '#3b82f6',
            borderLeftWidth: 4,
            borderLeftStyle: 'solid',
            padding: '20px 24px 12px',
          }}
        >
          <div className="flex items-center gap-2 mb-4">
            <div className="p-1.5 rounded-md" style={{ backgroundColor: formEdit ? (isDark ? 'rgba(245,158,11,0.08)' : '#fffbeb') : (isDark ? 'rgba(59,130,246,0.08)' : '#eff6ff') }}>
              {formEdit ? <Icons.Pencil className="w-4 h-4" style={{ color: '#f59e0b' }} /> : <Icons.Plus className="w-4 h-4" style={{ color: '#3b82f6' }} />}
            </div>
            <span className="text-sm font-semibold" style={{ color: formEdit ? '#f59e0b' : '#3b82f6' }}>
              {formEdit ? 'Edit Process Control' : 'Add New Process Control'}
            </span>
            {formEdit && <Tag color="warning" className="ml-2 text-xs">Editing</Tag>}
          </div>
          <Formik
            enableReinitialize
            initialValues={{
              parameter_name: editValues.parameter_name || '',
              data_type: editValues.data_type || '',
              unit_id: editValues.unit_id || null,
              control_category: editValues.control_category || '',
              control_category_id: editValues.control_category_id || null,
              sub_category_id: editValues.sub_category_id || null,
              setpoint: editValues.setpoint || '',
              lower_limit: editValues.lower_limit || '',
              upper_limit: editValues.upper_limit || '',
              data_source: editValues.data_source || '',
              criticality: editValues.criticality || '',
              machine_id: editValues.machine_id || null,
              location_id: editValues.location_id || null,
            }}
            validationSchema={validationSchema}
            onSubmit={(values, { resetForm }) => handleAdd(values, resetForm)}
          >
            {({ values, handleChange, setFieldValue, handleSubmit, errors, touched, handleReset }) => (
              <Form layout="vertical" onFinish={handleSubmit}>
                {/* Basic Info */}
                <Row gutter={16}>
                  <Col xs={24} md={8}>
                    <Form.Item
                      label={<span className="font-medium" style={{ color: textColor }}>Control Name</span>}
                      validateStatus={touched.parameter_name && errors.parameter_name ? 'error' : ''}
                      help={touched.parameter_name && errors.parameter_name ? errors.parameter_name : ''}
                    >
                      <Input
                        size="large"
                        name="parameter_name"
                        placeholder="e.g. Jacket Temperature"
                        value={values.parameter_name}
                        onChange={handleChange}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item
                      label={<span className="font-medium" style={{ color: textColor }}>Data Type</span>}
                      validateStatus={touched.data_type && errors.data_type ? 'error' : ''}
                      help={touched.data_type && errors.data_type ? errors.data_type : ''}
                    >
                      <Select
                        size="large"
                        showSearch
                        allowClear
                        placeholder="Select Data Type"
                        value={values.data_type || undefined}
                        onChange={(val) => setFieldValue('data_type', val)}
                        style={{ width: '100%' }}
                        options={[
                          { label: 'String', value: 'String' },
                          { label: 'Integer', value: 'Integer' },
                          { label: 'Float', value: 'Float' },
                          { label: 'Boolean', value: 'Boolean' },
                          { label: 'Date', value: 'Date' },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item
                      label={<span className="font-medium" style={{ color: textColor }}>Unit (UOM)</span>}
                      validateStatus={touched.unit_id && errors.unit_id ? 'error' : ''}
                      help={touched.unit_id && errors.unit_id ? errors.unit_id : ''}
                    >
                      <Select
                        size="large"
                        showSearch
                        allowClear
                        placeholder="e.g. °C, RPM, Bar"
                        value={values.unit_id}
                        onChange={(val) => setFieldValue('unit_id', val)}
                        filterOption={(input, option) =>
                          (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        style={{ width: '100%' }}
                      >
                        {unitList.map((item) => (
                          <Option key={item.id} value={item.id}>
                            {item.unit_name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                {/* Process Control Section */}
                <Divider orientation="left" orientationMargin={0}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Icons.SlidersHorizontal size={14} className="text-blue-600" />
                    <Typography.Text strong style={{ color: textColor, fontSize: 13 }}>
                      Process Control
                    </Typography.Text>
                  </div>
                </Divider>

                <Row gutter={16}>
                  {/* Control Category — dynamic from DB */}
                  <Col xs={24} md={8}>
                    <Form.Item label={<span className="font-medium" style={{ color: textColor }}>Control Category</span>}>
                      <Cascader
                        size="large"
                        allowClear
                        showSearch
                        placeholder="Select Control Category"
                        value={cascaderCatValue || undefined}
                        style={{ width: '100%' }}
                        popupClassName="cascader-wide-dropdown"
                        dropdownMenuColumnStyle={{ minWidth: 300 }}
                        onChange={(val, selectedOptions) => {
                          setCascaderCatValue(val || null);
                          setFieldValue('control_category_id', val?.[0] || null);
                          setFieldValue('sub_category_id', val?.[1] || null);
                          setFieldValue('control_category', selectedOptions?.map(o => o.label).join(' / ') || '');
                        }}
                        options={controlCategoryList.map((c) => ({
                          label: c.control_category_name,
                          value: c.control_category_id,
                          children: c.sub_categories?.length > 0
                            ? c.sub_categories.map((s) => ({
                                label: s.sub_category_name,
                                value: s.sub_category_id,
                              }))
                            : undefined,
                        }))}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item label={<span className="font-medium" style={{ color: textColor }}>Machine</span>}>
                      <Select
                        size="large"
                        showSearch
                        allowClear
                        placeholder="Select machine (optional)"
                        value={values.machine_id || undefined}
                        onChange={(val) => {
                          const selected = machineList.find(m => (m.machine_id || m.id) === val);
                          setFieldValue('machine_id', val || null);
                          setFieldValue('location_id', selected?.location_id || null);
                        }}
                        optionFilterProp="label"
                        options={machineList.map((m) => ({
                          value: m.machine_id || m.id,
                          label: m.machine_name || m.name,
                        }))}
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item label={<span className="font-medium" style={{ color: textColor }}>Value / Setpoint</span>}>
                      <Input
                        size="large"
                        name="setpoint"
                        placeholder="Target operating value"
                        value={values.setpoint}
                        onChange={handleChange}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={4}>
                    <Form.Item label={<span className="font-medium" style={{ color: textColor }}>Lower Limit</span>}>
                      <Input
                        size="large"
                        name="lower_limit"
                        type="number"
                        placeholder="e.g. 70"
                        value={values.lower_limit}
                        onChange={handleChange}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={4}>
                    <Form.Item label={<span className="font-medium" style={{ color: textColor }}>Upper Limit</span>}>
                      <Input
                        size="large"
                        name="upper_limit"
                        type="number"
                        placeholder="e.g. 80"
                        value={values.upper_limit}
                        onChange={handleChange}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item label={<span className="font-medium" style={{ color: textColor }}>Data Source</span>}>
                      <Select
                        size="large"
                        allowClear
                        placeholder="Select Data Source"
                        value={values.data_source || undefined}
                        onChange={(val) => setFieldValue('data_source', val)}
                        style={{ width: '100%' }}
                        options={[
                          { label: 'Manual Entry', value: 'Manual Entry' },
                          { label: 'PLC/SCADA Tag', value: 'PLC/SCADA Tag' },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item label={<span className="font-medium" style={{ color: textColor }}>Criticality</span>}>
                      <Select
                        size="large"
                        allowClear
                        placeholder="Select Criticality"
                        value={values.criticality || undefined}
                        onChange={(val) => setFieldValue('criticality', val)}
                        style={{ width: '100%' }}
                        options={[
                          { label: 'Critical (CPP)', value: 'Critical (CPP)' },
                          { label: 'Non-Critical', value: 'Non-Critical' },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item label=" " colon={false}>
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                        <Tooltip title="Reset">
                          <Button
                            icon={<Icons.Undo2 className="w-4 h-4" />}
                            onClick={() => {
                              handleReset();
                              setCascaderCatValue(null);
                              setFormEdit(false);
                              setEditValues({});
                              setShowForm(false);
                            }}
                          />
                        </Tooltip>
                        <Button
                          type="primary"
                          htmlType="submit"
                          icon={formEdit ? <Icons.Check className="w-4 h-4" /> : <Icons.Plus className="w-4 h-4" />}
                          style={formEdit ? { backgroundColor: '#f59e0b', borderColor: '#f59e0b' } : {}}
                        >
                          {formEdit ? 'Update' : 'Add'}
                        </Button>
                      </div>
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            )}
          </Formik>

          {/* Sub Control Category Manager */}
          <Divider orientation="left" orientationMargin={0} style={{ marginTop: 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Icons.Layers size={14} className="text-violet-600" />
              <Typography.Text strong style={{ color: textColor, fontSize: 13 }}>
                Sub Control Categories
              </Typography.Text>
            </div>
          </Divider>
          <div
            className="rounded-lg p-3"
            style={{
              backgroundColor: isDark ? 'rgba(139,92,246,0.06)' : '#faf5ff',
              border: '1px solid #e9d5ff',
            }}
          >
            {/* Existing sub categories */}
            <div className="flex flex-wrap gap-2 mb-3">
              {subCatLoading ? (
                <span className="text-xs text-gray-400">Loading...</span>
              ) : subCategoryList.length === 0 ? (
                <span className="text-xs text-gray-400">No sub categories yet</span>
              ) : (
                subCategoryList.map((sc) => (
                  <Tag key={sc.id} color="purple" className="text-xs px-2 py-0.5">
                    {sc.name}
                  </Tag>
                ))
              )}
            </div>
            {/* Add new sub category inline */}
            <div className="flex items-center gap-2">
              <Input
                size="middle"
                placeholder="e.g. Pressure, Temperature..."
                value={newSubCatName}
                onChange={(e) => setNewSubCatName(e.target.value)}
                onPressEnter={handleAddSubCategory}
                style={{ maxWidth: 260 }}
              />
              <Button
                type="primary"
                size="middle"
                loading={addingSubCat}
                icon={<Icons.Plus className="w-3.5 h-3.5" />}
                onClick={handleAddSubCategory}
                style={{ backgroundColor: '#7c3aed', borderColor: '#7c3aed' }}
                disabled={!newSubCatName.trim()}
              >
                Add Sub Category
              </Button>
            </div>
          </div>
        </div>
      )}

      {showForm && <Divider />}

      <DataTable
        tableKey="processControlTable"
        searchable
        bordered
        data={parameterData}
        columns={allColumns}
        rowKey="id"
        exportable
        exportFilename="process_controls"
        pagination={{ pageSize: 15 }}
        scrollX="max-content"
        height="calc(100vh - 320px)"
        locale={{
          emptyText: (
            <EmptyState
              title="No process controls yet"
              description="Add a process-control parameter to start monitoring limits during batches."
              actionLabel="Add Process Control"
              onAction={() => setShowForm(true)}
            />
          ),
        }}
      />
    </Card>
  );
};

export default ProcessControlConfig;
